import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { media } from '../theme/media';

import { ReactComponent as SplashDotsLeft } from '../images/icons/splash-dots-1.svg';
import { ReactComponent as SplashDotsRight } from '../images/icons/splash-dots-2.svg';
import { ReactComponent as SplashLeft } from '../images/icons/splash-left.svg';
import { ReactComponent as SplashRight } from '../images/icons/splash-right.svg';
import { ReactComponent as LargeDots } from '../images/icons/large-dots.svg';

const Container = styled.div`
  position: relative;
  overflow: hidden;
  background-color: ${({ color, theme }) => {
    if (theme.pageOverrides?.includes('Dark Mode')) {
      return colors.blue['800'];
    }

    switch (color) {
      case 'Blue':
        return colors.blue['500'];
      case 'Light Blue':
        return colors.blue['50'];
      default:
        return colors.white;
    }
  }};
`;

const largeDotStyles = css`
  position: absolute;
  width: ${rem(250)};
  height: auto;

  ${media('>medium')} {
    width: ${rem(370)};
    opacity: 1;
  }
`;

const largeDotLeftStyles = (variation) => {
  switch (variation) {
    case 'short':
      return css`
        left: 0;
        bottom: 0;
        transform: translateY(60%);
      `;
    case 'swapped':
      return css`
        top: 0;
        left: 0;
        transform: translateY(-60%);
      `;
    default:
      return css`
        left: 0;
        bottom: 0;
        transform: translateY(40%);
      `;
  }
};

const largeDotRightStyles = (variation) => {
  switch (variation) {
    case 'short':
      return css`
        top: 0;
        right: 0;
        transform: translateY(-60%);
      `;
    case 'swapped':
      return css`
        right: 0;
        bottom: 0;
        transform: translateY(60%);
      `;
    default:
      return css`
        top: 0;
        right: 0;
        transform: translateY(-40%);
      `;
  }
};

const splashStyles = css`
  position: absolute;
  max-width: none;
`;

const splashLeftStyles = css`
  width: auto;
  height: 100%;
  top: 0;
  right: 65%;

  ${media('>small')} {
    left: 0;
    right: auto;
  }
`;

const splashRightStyles = css`
  width: auto;
  height: 100%;
  top: 0;
  left: 65%;
  ${media('>small')} {
    right: 0;
    left: auto;
  }
`;

const splashDotsStyles = css`
  position: absolute;
  width: ${rem(250)};
  height: auto;

  ${media('>medium')} {
    width: ${rem(360)};
  }
`;

const splashDotsLeft = (variation) => {
  switch (variation) {
    case 'clipped':
      return css`
        right: 0;
        bottom: 0;
        `;
    default:
      return css`
      left: 0;
      bottom: 0;
      transform: translateX(-40%);
      ${media('>small')} {
        transform: none;
      }
      `;
  }
};

const splashDotsRight = (variation) => {
  switch (variation) {
    case 'clipped':
      return css`
        left: 0;
        top: 0;
        transform: translateX(-60%);
      `;
    default:
      return css`
        top: 0;
        right: 0;
        transform: translateX(40%);

        ${media('>small')} {
          transform: none;
        }
      `;
  }
};

const getPattern = (pattern, patternVariation) => {
  switch (pattern) {
    case 'Splash Dots':
      return (
        <>
          <SplashDotsLeft css={css`${splashDotsStyles} ${splashDotsLeft(patternVariation)}`} />
          <SplashDotsRight css={css`${splashDotsStyles} ${splashDotsRight(patternVariation)}`} />
        </>
      );
    case 'Large Dots':
      return (
        <>
          <LargeDots css={css`${largeDotStyles} ${largeDotRightStyles(patternVariation)}`} />
          <LargeDots css={css`${largeDotStyles} ${largeDotLeftStyles(patternVariation)}`} />
        </>
      );
    case 'Splash':
      return (
        <>
          <SplashLeft css={css`${splashStyles} ${splashLeftStyles}`} />
          <SplashRight css={css`${splashStyles} ${splashRightStyles}`} />
        </>
      );
    default:
      return null;
  }
};

const Background = ({ color, pattern, patternVariation, className, children }) => {
  if (!pattern) return <Container color={color} className={className}>{children}</Container>;

  return (
    <Container color={color} className={className}>
      {children}
      {getPattern(pattern, patternVariation)}
    </Container>
  );
};

export default Background;
